import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { Observable, catchError, finalize, map, shareReplay, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';


/**
 * Extracts the path from a given URL string. If the URL is absolute, it uses the URL API to parse it.
 * If the URL is relative, it manually extracts the path portion. This is used to determine if the request
 * should have the Authorization header attached based on its path.
 *
 * @param url
 * @author sadatmalik
 */
function requestPath(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    const idx = url.indexOf('://');
    if (idx !== -1) {
      const afterHost = url.indexOf('/', idx + 3);
      return afterHost === -1 ? '/' : url.slice(afterHost);
    }
    return url.startsWith('/') ? url : `/${url}`;
  }
}

/**
 * Determines whether the Authorization header should be attached to a request based on its URL. The
 * header is attached to all requests that start with '/api/' except those that start with
 * '/api/auth', which are assumed to be authentication-related endpoints that do not require the
 * token.
 *
 * @param url
 * @author sadatmalik
 */
function isProtectedApiRequest(url: string): boolean {
  const path = requestPath(url);
  return path.startsWith('/api/') && !path.startsWith('/api/auth');
}

const RETRY_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);
let refreshInFlight$: Observable<string> | null = null;

/**
 * Interceptor function that attaches the Authorization header with the access token to outgoing
 * HTTP requests that match certain criteria. It also handles 401 Unauthorized responses by
 * attempting to refresh the access token and retrying the failed request once. If the refresh
 * fails, it clears the session and redirects to the login page.
 *
 * @param req
 * @param accessToken
 * @param markRetried
 * @author sadatmalik
 */
function withAuthHeader(req: HttpRequest<unknown>, accessToken: string | null, markRetried = false): HttpRequest<unknown> {
  const context = markRetried ? req.context.set(RETRY_AFTER_REFRESH, true) : req.context;
  if (!accessToken) {
    return req.clone({ context });
  }
  return req.clone({
    context,
    setHeaders: { Authorization: `Bearer ${accessToken}` }
  });
}

/**
 * Resolves the return URL for redirection after a successful login. It constructs the URL from the
 * current window location, including the pathname, search parameters, and hash. If the code is
 * running in a non-browser environment (where `window` is undefined), it defaults to '/today'.
 *
 * @returns The resolved return URL as a string.
 * @author sadatmalik
 */
function resolveReturnUrl(): string {
  if (typeof window === 'undefined') {
    return '/today';
  }
  return `${window.location.pathname}${window.location.search}${window.location.hash}` || '/today';
}

/**
 * Performs the token refresh operation by calling the AuthService's refresh method. It ensures that
 * only one refresh request is in flight at a time by sharing the observable. If the refresh fails,
 * it clears the session and redirects to the login page.
 *
 * @param auth
 * @param router
 */
function refreshAccessToken(auth: AuthService, router: Router): Observable<string> {
  if (!refreshInFlight$) {
    refreshInFlight$ = auth.refresh().pipe(
      map((response) => response.accessToken),
      catchError((err) => {
        auth.clearSession();
        void router.navigate(['/login'], {
          queryParams: { returnUrl: resolveReturnUrl() }
        });
        return throwError(() => err);
      }),
      finalize(() => {
        refreshInFlight$ = null;
      }),
      shareReplay(1)
    );
  }
  return refreshInFlight$;
}

/**
 * HTTP interceptor function that attaches the Authorization header to outgoing requests when
 * appropriate and handles 401 Unauthorized responses by attempting to refresh the access token and
 * retrying the request. It uses the AuthService to manage authentication state and the Router to
 * handle redirection on session expiration.
 *
 * @param req
 * @param next
 * @author sadatmalik
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const requestWithAuth = isProtectedApiRequest(req.url)
    ? withAuthHeader(req, auth.getAccessToken())
    : req;

  return next(requestWithAuth).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }
      if (error.status !== 401) {
        return throwError(() => error);
      }
      if (!isProtectedApiRequest(req.url) || req.context.get(RETRY_AFTER_REFRESH)) {
        return throwError(() => error);
      }

      return refreshAccessToken(auth, router).pipe(
        switchMap((token) => next(withAuthHeader(req, token, true)))
      );
    })
  );
};
